import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import Reveal, { SectionHead } from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];

const FAQS = [
    {
        q: "Does fynary connect to my bank account?",
        a: "No. There is no bank syncing. You export a statement CSV from your bank yourself and import it into fynary. fynary. never asks for bank passwords, PINs or OTPs.",
    },
    {
        q: "Which banks work with fynary?",
        a: "fynary is built for CSV statements from major Indian banks — HDFC, SBI, ICICI, Axis and Kotak, among others. Files can be up to 5 MB and must be .csv format.",
    },
    {
        q: "How does the Ask fynary assistant work?",
        a: "Ask fynary is a spending assistant that answers questions about your own imported transactions — things like “Where did my money go this month?”, “Which category spiked compared to last month?” or “How much did I save this month?”. It only uses your own data to answer.",
    },
    {
        q: "What will I see in the app?",
        a: "A monthly snapshot (spent, income, savings and your largest expense), a six-month trend view, your transactions with manual entry, insights, and the Ask fynary assistant.",
    },
    {
        q: "Is my financial data private?",
        a: "Your statement is imported by you and stays private to you. And fynary. never asks for bank passwords, PINs or OTPs — the app simply doesn't need them.",
    },
    {
        q: "Can I add transactions manually?",
        a: "Yes. Alongside statement imports, fynary has a manual transaction button — log spending by hand whenever something doesn't come from a CSV.",
    },
    {
        q: "How do I get the fynary app?",
        a: "fynary is on iOS and Android. Write to workfynary@gmail.com or follow @fynary.in on Instagram for download links and updates.",
    },
];

export default function Faq() {
    const [open, setOpen] = useState(0);

    return (
        <section id="faqs" className="scroll-mt-24 border-t border-white/5 py-20 sm:py-24 lg:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                <SectionHead
                    eyebrow="FAQs"
                    title="Straight answers."
                    sub="Everything people ask before their first import."
                    testid="faq-heading"
                />

                <div className="mx-auto mt-12 max-w-3xl">
                    {FAQS.map((f, i) => (
                        <Reveal key={f.q} delay={0.04 * i}>
                            <div
                                data-testid={`faq-accordion-item-${i}`}
                                className="mb-3 overflow-hidden rounded-2xl border border-white/10 bg-fynary-surface transition-[border-color] duration-200 hover:border-white/20"
                            >
                                <button
                                    data-testid={`faq-accordion-trigger-${i}`}
                                    onClick={() => setOpen(open === i ? -1 : i)}
                                    className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left sm:px-6"
                                    aria-expanded={open === i}
                                >
                                    <span className="font-display text-base font-semibold text-white sm:text-lg">{f.q}</span>
                                    <Plus
                                        className={`h-5 w-5 shrink-0 text-fynary-lime transition-transform duration-300 ${
                                            open === i ? "rotate-45" : ""
                                        }`}
                                    />
                                </button>
                                <AnimatePresence initial={false}>
                                    {open === i && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: EASE }}
                                            className="overflow-hidden"
                                        >
                                            <p className="px-5 pb-6 text-sm leading-relaxed text-fynary-muted sm:px-6 sm:text-base">{f.a}</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
